import type { ApiClient } from '../client';

export interface IframePoolItem {
  id: number;
  title: string;
  imageUrl: string | null;
  subImageUrl: string | null;
  itemPrice: number;
}

export interface IframePoolItemsPage {
  items: IframePoolItem[];
  totalCount: number;
  page: number;
  per: number;
}

export interface IframePoolItemsParams {
  page?: number;
  per?: number;
}

interface RawPoolItem {
  id?: number | string;
  title?: string | null;
  image_url?: string | null;
  sub_image_url?: string | null;
  item_price?: number | string | null;
}

interface RawMeta {
  total_count?: number;
  page?: number;
  per?: number;
}

/**
 * Paginated "heavy hitter" pool_items of an offer, for the Potential Hits
 * grid. Backend sorts by fair_market_value DESC, filters to engagement_status
 * "listed". Mirrors api/shared/v1 meta shape.
 */
import {
  mapPoolItemEmbed,
  type IframePoolOrderItem,
  type RawPoolItemEmbed,
} from './poolOrders';

/**
 * Marks a single pool_item as opened (reveal step). Returns the full
 * IframePoolOrderItem — the backend at this point has `best_active_offer`
 * attached (OpenAndOffer::Run creates it), so the reveal UI can show the
 * Buyback panel without a second round-trip.
 */
export async function markPoolItemAsOpened(
  client: ApiClient,
  poolItemId: string | number,
): Promise<IframePoolOrderItem> {
  const raw = await client.request<{ pool_item: RawPoolItemEmbed }>(
    `/loot_machine/api/game_platform/iframe/v1/pool_items/${encodeURIComponent(String(poolItemId))}/mark_as_opened`,
    { method: 'PUT' },
  );
  return mapPoolItemEmbed(raw.pool_item ?? {});
}

export async function getIframePoolOfferItems(
  client: ApiClient,
  poolOfferId: string | number,
  params: IframePoolItemsParams = {},
): Promise<IframePoolItemsPage> {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.per) qs.set('per', String(params.per));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';

  const raw = await client.request<{ pool_items: RawPoolItem[]; meta?: RawMeta }>(
    `/loot_machine/api/game_platform/iframe/v1/pool_offers/${encodeURIComponent(String(poolOfferId))}/pool_items${suffix}`,
    { method: 'GET' },
  );

  const items = (raw.pool_items ?? []).map((i) => ({
    id: Number(i.id ?? 0),
    title: (i.title ?? '').toString(),
    imageUrl: typeof i.image_url === 'string' && i.image_url ? i.image_url : null,
    subImageUrl: typeof i.sub_image_url === 'string' && i.sub_image_url ? i.sub_image_url : null,
    itemPrice: Number(i.item_price ?? 0),
  }));

  return {
    items,
    totalCount: Number(raw.meta?.total_count ?? items.length),
    page: Number(raw.meta?.page ?? 1),
    per: Number(raw.meta?.per ?? items.length),
  };
}
